import {Injectable} from "@angular/core";
import {areCompetitorResultsEqual, CompetitorFilter, CompetitorResults, Gender, PagedFilter, Race} from "@model";
import {BehaviorSubject} from "rxjs";
import {LiveResultsService} from "./live-results.service";
import {CompetitorResultsService} from "./competitor-results.service";


@Injectable({ providedIn: "root" })
export class LiveControlService {
  static readonly TIMEOUT_30 = 30000;
  static readonly TIMEOUT_4 = 6000;
  static readonly MAX_TIMEOUT = 2_147_483_647; // Max 32-bit signed integer in ms

  // Map to store interval IDs for different fetching types (all, male, female)
  private intervals: Map<"all" | "male" | "female" | "finished" | "started", NodeJS.Timeout> = new Map();

  private _liveResult = new BehaviorSubject<CompetitorResults[] | undefined | null>(null);
  private _liveResultMale = new BehaviorSubject<CompetitorResults[] | undefined | null>(null);
  private _liveResultFemale = new BehaviorSubject<CompetitorResults[] | undefined | null>(null);
  private _finishedCount = new BehaviorSubject<number | undefined | null>(null);
  private _startedCount = new BehaviorSubject<number | undefined | null>(null);

  constructor(private resultsService: LiveResultsService, private competitorResultsService: CompetitorResultsService) {}

  get liveResults() {
    return this._liveResult;
  }
  get liveResultsFemale() {
    return this._liveResultFemale;
  }
  get liveResultsMale() {
    return this._liveResultMale;
  }
  get finishedCount() {
    return this._finishedCount;
  }

  get startedCount() {
    return this._startedCount;
  }

  clearResults() {
    this.liveResults.next(undefined);
    this.liveResultsFemale.next(undefined);
    this.liveResultsMale.next(undefined);
  }

  startFetchingResults(race: Race | undefined, filter: PagedFilter<CompetitorFilter>, all: boolean = true) {
    this.clearAll();
    // Check if the race has valid start and end times
    if (race?.raceStart && race?.raceEnd) {
      const raceStart = new Date(race.raceStart).getTime();
      const raceEnd = new Date(race.raceEnd).getTime();
      const now = Date.now();

      if (now >= raceStart) {
        // If race has started, fetch the results immediately
        this.startFetchingCompetitorsResults(filter, all);
        this.startFetchingFinishedCount(filter);
        this.startFetchingStartedCount(filter);
        // Schedule the finalization process when the race ends + 30 seconds
        this.scheduleFinalize(raceEnd + LiveControlService.TIMEOUT_30 - now, filter, all);
      } else {
        this.scheduleStart(raceStart - now, filter, all);
        this.scheduleFinalize(raceEnd + LiveControlService.TIMEOUT_30 - now, filter, all);
      }
    }
  }

  // Start fetching results for the given type (all, male, female)
  private startFetchingCompetitorsResults(filter: PagedFilter<CompetitorFilter>, all: boolean = true) {
      if (all) {
        this.fetchResults("all", filter);
        this.startInterval("all", filter, LiveControlService.TIMEOUT_4);
      } else {
        this.fetchResults("male", filter);
        this.startInterval("male", filter, LiveControlService.TIMEOUT_4);
        this.fetchResults("female", filter);
        this.startInterval("female", filter, LiveControlService.TIMEOUT_4);
      }
  }

  private startFetchingFinishedCount(filter: PagedFilter<CompetitorFilter>) {
    this.fetchFinishCount(filter)
    this.intervals.set("finished", setInterval(() => this.fetchFinishCount(filter), LiveControlService.TIMEOUT_4));
  }

  private startFetchingStartedCount(filter: PagedFilter<CompetitorFilter>) {
    this.fetchStartedCount(filter)
    this.intervals.set("started", setInterval(() => this.fetchStartedCount(filter), LiveControlService.TIMEOUT_4));
  }

  private startInterval(type: "all" | "male" | "female", filter: PagedFilter<CompetitorFilter>, interval: number) {
    this.intervals.set(type, setInterval(() => this.fetchResults(type, filter), interval));
  }

  fetchResults(type: "all" | "male" | "female", pagedFilter: PagedFilter<CompetitorFilter>, finalize: boolean = false) {
    if (type === "male" || type === "female") {
      pagedFilter = { ...pagedFilter, filter: { ...pagedFilter.filter, gender: type === "male" ? Gender.MALE : Gender.FEMALE} };
    }
    if (!finalize) {
      this.resultsService.getCompetitorLiveResults(pagedFilter).subscribe((results) => {
        this.saveResults(type, results)
      });
    }
    else {
      this.competitorResultsService.getCompetitorResults(pagedFilter).subscribe(results => {
        this.saveResults(type, results)
      })
    }
  }

  // Compare the current results with the new results, if changed, update the BehaviorSubject
  saveResults(type: "all" | "male" | "female", results: CompetitorResults[] | undefined) {
    const subject: BehaviorSubject<CompetitorResults[] | undefined | null> = type === "female" ? this._liveResultFemale : (type === "male" ? this._liveResultMale : this._liveResult)
    const current: CompetitorResults[] | undefined | null = subject.value;
    let changed = false;
    if (!!results?.length && !!current?.length && results?.length === current.length) {
      for (let i = 0; i < current.length; i++) {
        if (!areCompetitorResultsEqual(results[i], current[i])) {
          changed = true;
          break;
        }
      }
    }
    else if ((results?.length ?? 0) !== (current?.length ?? 0)) {
      changed = true
    }
    if (changed) {
      subject.next(results);
    }
  }


  stopFetching() {
      this.intervals.forEach((intervalId, key) => {
        clearInterval(intervalId);
        this.intervals.delete(key);
      });
    }


  scheduleStart(delay: number, filter: PagedFilter<CompetitorFilter>, all: boolean = true) {
    if (delay > LiveControlService.MAX_TIMEOUT) {
      setTimeout(() => this.scheduleStart(delay - LiveControlService.MAX_TIMEOUT, filter, all), LiveControlService.MAX_TIMEOUT);
    } else {
      setTimeout(() => {
        this.startFetchingCompetitorsResults(filter, all)
        this.startFetchingFinishedCount(filter);
        this.startFetchingStartedCount(filter);
      }, delay);
    }
  }

  fetchFinishCount(pagedFilter: PagedFilter<CompetitorFilter>) {
    this.resultsService.getFinishedCount(pagedFilter.filter?.raceId).subscribe(count => {
      this.finishedCount.next(count)
    })
  }

  fetchStartedCount(pagedFilter: PagedFilter<CompetitorFilter>) {
    this.resultsService.getStartedCount(pagedFilter.filter?.raceId).subscribe(count => {
      this.startedCount.next(count)
    })
  }

  scheduleFinalize(timeAfterRaceEnd: number, filter: PagedFilter<CompetitorFilter>, all: boolean = true) {
    if (timeAfterRaceEnd > LiveControlService.MAX_TIMEOUT) {
      setTimeout(() => this.scheduleFinalize(timeAfterRaceEnd - LiveControlService.MAX_TIMEOUT, filter), LiveControlService.MAX_TIMEOUT, all);
    } else {
      setTimeout(() => {
        this.stopFetching();
        if (all) {
          this.fetchResults("all", filter, true);
        } else {
          this.fetchResults("male", filter, true);
          this.fetchResults("female", filter, true);
        }
        this.fetchFinishCount(filter);
        this.fetchStartedCount(filter);
      }, timeAfterRaceEnd);
    }
  }

  clearAll() {
    this.stopFetching();
    this.clearResults();
  }

  ngOnDestroy() {
    this.clearAll();
    this.finishedCount.next(undefined);
    this.startedCount.next(undefined);
  }
}
